/* eslint-disable @typescript-eslint/no-unused-vars */
import { call, put, select, delay, takeLatest, take } from 'redux-saga/effects'
import store from 'src/store'
import { createSagas } from 'src/helpers/redux'
import { IResponse } from 'src/services/storage/project/http'
import * as state from './state'
import { actions, Actions, PREFIX } from './actions'
import { setters } from './index'
import { selectors } from './selectors'
import { selectors as accountSelectors } from 'src/store/modules/account/selectors'

import { nanoid } from 'nanoid'
import * as agentsService from 'src/services/agents'

import {
  CRMAgent,
  mastraClientAuth,
  mastraClentCancel,
} from 'src/services/agents/client'
import { tools } from 'src/services/agents/tools'
import * as app from 'src/store/modules/app'
import {
  IChunkSink,
  createChunkHandler,
  findPendingApproval,
  isApprovalText,
  pickPlanApprovalRun,
} from './planApproval'

const toastId = 'assistant/message'

const getToolStatusLabel = (toolName?: string) => {
  switch (toolName) {
    case 'global-search':
      return 'searching CRM'
    case 'navigate-to-object':
      return 'opening record'
    case 'send-email':
      return 'opening email draft'
    case 'send-sms':
      return 'opening message draft'
    case 'make-phone-call':
      return 'preparing call'
    default:
      return 'working'
  }
}

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error)

/** The server answered 404: the suspended run is gone and cannot be resumed. */
const isGoneRunError = (error: unknown): boolean =>
  typeof error === 'object' &&
  error !== null &&
  (error as { status?: unknown }).status === 404

const createAssistantMessage = (): state.IChatMessage => ({
  id: `assistant-${Date.now()}`,
  role: 'assistant',
  content: '',
  createdAt: new Date().toISOString(),
})

/**
 * Routes the effects of stream chunks for one assistant message into the
 * store. Shared by the initial stream and the resume (continuation) stream.
 */
const createMessageSink = (
  threadId: string,
  message: Pick<state.IChatMessage, 'id' | 'role' | 'createdAt'>
): IChunkSink => ({
  update: (patch) =>
    store.dispatch(
      setters.setThreadsItemMessagesUpdate({
        threadId,
        message: { ...message, ...patch },
      })
    ),
  setPendingApproval: (pendingApproval) =>
    store.dispatch(
      setters.setThreadsItemMessagePendingApproval({
        threadId,
        messageId: message.id,
        pendingApproval,
      })
    ),
  clearPendingApproval: (toolCallId) =>
    store.dispatch(
      setters.setThreadsItemMessagePendingApprovalClear({
        threadId,
        toolCallId,
      })
    ),
})

/** Shows a failed stream like the `error` chunk does, keeping partial text. */
const failMessage = (sink: IChunkSink, content: string, error: unknown) => {
  console.error('❌ Stream error:', error)
  sink.update({
    streamingText: undefined,
    content: content || `Stream error: ${getErrorMessage(error)}`,
  })
}

/**
 * Re-attaches a plan approval card after the thread history is loaded, when
 * the agents service still holds a suspended run for this thread.
 */
function* restorePendingApproval(
  threadId: string,
  resourceId: string,
  messages: state.IChatMessage[]
) {
  try {
    mastraClientAuth()
    const response: Awaited<ReturnType<typeof CRMAgent.listSuspendedRuns>> =
      yield call(() => CRMAgent.listSuspendedRuns({ threadId, resourceId }))

    const pendingApproval = pickPlanApprovalRun(response?.runs)
    if (!pendingApproval) return

    const lastAssistantMessage = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant')

    if (lastAssistantMessage) {
      yield put(
        setters.setThreadsItemMessagePendingApproval({
          threadId,
          messageId: lastAssistantMessage.id,
          pendingApproval,
        })
      )
    } else {
      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId,
          message: {
            ...createAssistantMessage(),
            id: `assistant-plan-${pendingApproval.runId}`,
            pendingApproval,
          },
        })
      )
    }
  } catch (error) {
    // Older agents services have no suspended-runs endpoint; history must load anyway.
    console.debug(error)
  }
}

const sagas = createSagas<Actions>(actions, {
  openWindow: function* ({ payload }) {
    yield put(setters.setWindow({ open: true }))
    const isStreaming: boolean = yield select(selectors.isStreaming)
    if (isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
  },
  closeWidow: function* ({ payload }) {
    yield put(setters.setWindow({ open: false }))
  },
  toogleWindow: function* ({ payload }) {
    const isWindowOpen: boolean = yield select(selectors.windowOpen)
    const isStreaming: boolean = yield select(selectors.isStreaming)

    yield put(setters.setWindow({ open: !isWindowOpen }))

    if (!isWindowOpen && isStreaming) {
      yield put(
        app.actions.toastDismiss.pending({
          id: toastId,
        })
      )
    }
    if (isWindowOpen && isStreaming) {
      yield put(
        app.actions.tost.pending({
          id: toastId,
          type: 'progress',
          icon: 'mingcute:ai-fill',
          title: 'Assistant',
          description: 'Writes a message...',
        })
      )
    }
  },
  updateWindow: function* ({ payload }) {
    yield put(setters.setWindow(payload))
  },
  updateContext: function* ({ payload }) {
    yield put(setters.setContext(payload))
  },
  sendMessage: function* ({ payload }) {
    const threads: state.IChatThread[] = yield select(selectors.threads)
    const pendingApproval = findPendingApproval(threads, payload.threadId)

    // A plan card is waiting: the text answers it instead of starting a new run.
    if (pendingApproval) {
      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          message: {
            id: nanoid(),
            role: 'user',
            content: payload.text,
            createdAt: new Date().toISOString(),
          },
        })
      )

      const approved = isApprovalText(payload.text)
      yield put(
        actions.resumePlan.pending({
          threadId: payload.threadId,
          approved,
          comment: approved ? undefined : payload.text,
        })
      )

      return
    }

    const accountId: number = yield select(accountSelectors.userId)

    yield put(
      actions.streamMessage.pending({
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
        threadId: payload.threadId,
        role: 'user',
        content: payload.text,
      })
    )
  },

  getMemoryThreadsPaginated: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const response: IResponse<{ threads: state.IChatThread[] }> = yield call(
      agentsService.getMemoryThreadsPaginated,
      {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      }
    )

    yield put(setters.setThreads(response.data.threads))

    const [defaultThread] = response.data.threads

    if (!defaultThread) {
      const newDefaultThread: IResponse<state.IChatThread> = yield call(
        agentsService.postMemoryThreads,
        {
          resourceId: accountId.toString(),
          agentId: agentsService.CRM_AGENT_ID,
          title: 'Default',
        }
      )

      yield put(setters.setThreadsNewItem(newDefaultThread.data))
    }
  },
  postMemoryThreads: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPostMemoryThreadsData> = yield call(
      agentsService.postMemoryThreads,
      {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      }
    )

    // yield put(setters.setThreadsNewItem(thread.data))
  },
  patchMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const thread: IResponse<agentsService.IPatchMemoryThreadsItemData> =
      yield call(agentsService.patchMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsUpdate(thread.data))
  },
  deleteMemoryThreadsItem: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)

    const thread: IResponse<agentsService.IDeleteMemoryThreadsItemData> =
      yield call(agentsService.deleteMemoryThreadsItem, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    // yield put(setters.setThreadsRemove(payload))
  },
  getMemoryThreadsItemMessages: function* ({ payload }) {
    const accountId: number = yield select(accountSelectors.userId)
    const threadMessages: IResponse<agentsService.IGetMemoryThreadsItemMessagesData> =
      yield call(agentsService.getMemoryThreadsItemMessages, {
        ...payload,
        resourceId: accountId.toString(),
        agentId: agentsService.CRM_AGENT_ID,
      })

    const formattedMessages: state.IChatMessage[] =
      threadMessages.data.messages.map((msg: any) => {
        let content: string

        if (typeof msg.content === 'string') {
          content = msg.content
        } else if (msg.content && typeof msg.content === 'object') {
          if ('parts' in msg.content && Array.isArray(msg.content.parts)) {
            content = msg.content.parts
              .filter((part: any) => {
                if (typeof part === 'string') return true
                if (part.type === 'text') return true

                return false
              })
              .map((part: any) => {
                if (typeof part === 'string') return part
                if (part.text) return part.text

                return ''
              })
              .filter((text: string) => text.trim().length > 0)
              .join('\n')
          } else if (
            'content' in msg.content &&
            typeof msg.content.content === 'string'
          ) {
            content = msg.content.content
          } else {
            content = msg.content.text || msg.content.content || ''
          }
        } else {
          content = String(msg.content || '')
        }

        if (!content.trim() && msg.role === 'assistant') {
          content = '(Agent processed your request)'
        }

        return {
          id: msg.id,
          role: msg.role as 'user' | 'assistant',
          content,
          createdAt: msg.createdAt,
        }
      })

    yield put(
      setters.setThreadsItemMessages({
        threadId: payload.threadId,
        messages: formattedMessages,
      })
    )

    yield call(
      restorePendingApproval,
      payload.threadId,
      accountId.toString(),
      formattedMessages
    )
  },

  logging: {
    effect: (pattern, saga) => takeLatest('*', saga),
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX) && !data.type.includes('cacheStorage')) {
        yield put(actions.cacheStorage.pending())
      }
    },
  },
  cacheStorage: {
    effect: 'latest',
    controlled: true,
    saga: function* (data) {
      if (data.type.includes(PREFIX)) {
        yield delay(1000)
        try {
          const stateData: state.IStateData = yield select(selectors.data)
          localStorage.setItem(PREFIX, JSON.stringify(stateData))
        } catch (e) {
          console.debug(e)
        }
      }
    },
  },
  streamMessageCancel: function* ({ payload }) {
    yield put(setters.setStreamMessagesCancel(payload))
    try {
      mastraClentCancel()
    } catch (e) {}
  },
  streamMessage: function* ({ payload }) {
    try {
      mastraClientAuth()
      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          message: {
            id: nanoid(),
            role: 'user',
            content: payload.content,
            createdAt: new Date().toISOString(),
          },
        })
      )

      const assistantMessage = createAssistantMessage()

      yield put(
        setters.setThreadsItemMessagesAppend({
          threadId: payload.threadId,
          message: {
            ...assistantMessage,
            streamingText: 'Thinking...',
          },
        })
      )

      const streamOptions = {
        clientTools: tools as any,
        memory: {
          thread: payload.threadId,
          resource: payload.resourceId,
        },
      }

      const assistantContext: state.IAssistantContext | undefined =
        yield select(selectors.context)
      const streamContent = assistantContext?.prompt
        ? `${assistantContext.prompt}\n\nUser request:\n${payload.content}`
        : payload.content

      const sink = createMessageSink(payload.threadId, assistantMessage)
      const onChunk = createChunkHandler(sink)

      CRMAgent.stream(streamContent, streamOptions)
        .then((stream) => stream.processDataStream({ onChunk }))
        .catch((error) => failMessage(sink, onChunk.getContent(), error))
    } catch (e) {
      console.debug(e)
    }
  },
  /**
   * Answers a pending plan approval card: resumes the suspended run with the
   * user's decision and streams the agent's report into a new reply.
   */
  resumePlan: function* ({ payload }) {
    const { threadId, approved, comment } = payload
    const threads: state.IChatThread[] = yield select(selectors.threads)
    const pendingApproval = findPendingApproval(threads, threadId)

    if (!pendingApproval) {
      throw new Error('No plan is waiting for approval in this thread')
    }

    mastraClientAuth()

    const assistantMessage = createAssistantMessage()
    yield put(
      setters.setThreadsItemMessagesAppend({
        threadId,
        message: {
          ...assistantMessage,
          streamingText: 'Thinking...',
        },
      })
    )

    const sink = createMessageSink(threadId, assistantMessage)
    const onChunk = createChunkHandler(sink)

    const resumeData: Record<string, string | boolean> = { approved }
    if (comment !== undefined) {
      resumeData.comment = comment
    }

    try {
      const stream: Awaited<ReturnType<typeof CRMAgent.resumeStream>> =
        yield call(() =>
          CRMAgent.resumeStream(resumeData, {
            runId: pendingApproval.runId,
            toolCallId: pendingApproval.toolCallId,
            clientTools: tools as any,
          })
        )

      // The server accepted the decision: the card is no longer actionable.
      yield put(
        setters.setThreadsItemMessagePendingApprovalClear({
          threadId,
          toolCallId: pendingApproval.toolCallId,
        })
      )

      yield call(() => stream.processDataStream({ onChunk }))
    } catch (error) {
      failMessage(sink, onChunk.getContent(), error)

      if (isGoneRunError(error)) {
        yield put(
          setters.setThreadsItemMessagePendingApprovalClear({
            threadId,
            toolCallId: pendingApproval.toolCallId,
          })
        )
      }

      throw error
    }
  },
})

export default sagas
